import { ColumnDef, ColumnFiltersState, GroupingState, PaginationState, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { FilterOptions, InitialFilterModel } from "../ReactBaseTable";

const columnFiltersStorageIdentifier = `tableColumnFilters`;
const columnVisibilityStorageIdentifier = `tableColumnVisibility`;
const sortingStorageIdentifier = `tableSorting`;
const paginationStorageIdentifier = `tablePagination`;
const columnOrderStorageIdentifier = `tableColumnOrder`;

const getStorageKey = (tableId: string, storageIdentifier: string) => `${tableId}_${storageIdentifier}`

// Remove filters with empty/nullish values that would otherwise filter out all rows
// (e.g. range filters persisted as [null, null] from SearchNumberField mount-onChange).
const sanitizeColumnFilters = (filters: ColumnFiltersState): ColumnFiltersState =>
    filters.filter(f => {
        if (f.value === undefined || f.value === null || f.value === '') return false;
        if (Array.isArray(f.value)) {
            return f.value.some(v => v !== undefined && v !== null && v !== '');
        }
        return true;
    });


const useReactBaseTableState = <T> (tableId: string, columns: ColumnDef<T>[], filterOptions: FilterOptions) => {

  const tableColumnFiltersStorageKey = getStorageKey(tableId, columnFiltersStorageIdentifier);
  const tableColumnVisibilityStorageKey = getStorageKey(tableId, columnVisibilityStorageIdentifier);
  const tableSortingStorageKey = getStorageKey(tableId, sortingStorageIdentifier);
  const tablePaginationStorageKey = getStorageKey(tableId, paginationStorageIdentifier);
  const tableColumnOrderStorageKey = getStorageKey(tableId, columnOrderStorageIdentifier);

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
      const savedFilters = localStorage.getItem(tableColumnFiltersStorageKey);
      // Use initial state if set
      return filterOptions.initialFilter?.length
        ? filterOptions.initialFilter.map((filterOptions: InitialFilterModel) => ({
            id: filterOptions.initialFilterId,
            value: filterOptions.initialFilterValue,
        }))
        :
        savedFilters
          ? sanitizeColumnFilters(JSON.parse(savedFilters))
          : [];
    });

    const [columnVisibility, setColumnVisibility] = useState(() => {
      const savedVisibility = localStorage.getItem(tableColumnVisibilityStorageKey);
      return savedVisibility ? JSON.parse(savedVisibility) : {};
    });

    const [sorting, setSorting] = useState<SortingState>(() => {
      const savedSorting = localStorage.getItem(tableSortingStorageKey);
      return savedSorting ? JSON.parse(savedSorting) : filterOptions.initialSortColumn ? [{
          id: filterOptions.initialSortColumn,
          desc: false
      }] : [];
    });

    const [pagination, setPagination] = useState<PaginationState>(() => {
      const savedPagination = localStorage.getItem(tablePaginationStorageKey);
      return savedPagination ? JSON.parse(savedPagination) : {
          pageIndex: 0,
          pageSize: filterOptions.pageSizeOptions[0],
      };
    });

    const [columnOrder, setColumnOrder] = useState<string[]>(() => {
      const savedColumnOrder = localStorage.getItem(tableColumnOrderStorageKey);
      return savedColumnOrder ? JSON.parse(savedColumnOrder) : columns.map(c => c.id!);
    });

    const [grouping, setGrouping] = useState<GroupingState>([]);

    useEffect(() => {
        localStorage.setItem(tableColumnFiltersStorageKey, JSON.stringify(columnFilters));
    }, [columnFilters, tableColumnFiltersStorageKey]);

    useEffect(() => {
        localStorage.setItem(tableColumnVisibilityStorageKey, JSON.stringify(columnVisibility));
    }, [columnVisibility, tableColumnVisibilityStorageKey]);

    useEffect(() => {
        localStorage.setItem(tableSortingStorageKey, JSON.stringify(sorting));
    }, [sorting, tableSortingStorageKey]);

    useEffect(() => {
        localStorage.setItem(tablePaginationStorageKey, JSON.stringify(pagination));
    }, [pagination, tablePaginationStorageKey]);

    useEffect(() => {
        localStorage.setItem(tableColumnOrderStorageKey, JSON.stringify(columnOrder));
    }, [columnOrder, tableColumnOrderStorageKey]);

    return {
      columnFilters,
      setColumnFilters,
      columnVisibility,
      setColumnVisibility,
      sorting,
      setSorting,
      pagination,
      setPagination,
      columnOrder,
      setColumnOrder,
      grouping,
      setGrouping,
    }
}

export default useReactBaseTableState;